<template>
  <div class="profile-container page-container bg-light-gradient">
    <div class="container-padded py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2"><i class="fas fa-user-circle"></i></span> Mein Profil
        </h1>
        <router-link :to="userIsSuperAdmin ? '/admin/dashboard' : '/company/dashboard'" class="btn btn-outline-secondary btn-with-icon animate-fade-in with-hover-effect">
          <span class="icon me-2"><i class="fas fa-arrow-left"></i></span> Zurück zum Dashboard
        </router-link>
      </div>
      
      <div class="row g-4">
        <div class="col-lg-8">
          <!-- Persönliche Daten Card -->
          <Card animateIn="fade-in-up" class="card-with-form">
            <template v-slot:header>
              <h4 class="card-title mb-0">Persönliche Daten</h4>
            </template>
            
            <div v-if="loading" class="text-center py-4 content-loading">
              <div class="loader loader-lg mb-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Wird geladen...</span>
                </div>
              </div>
              <p class="text-muted">Profildaten werden geladen...</p>
            </div>
            
            <form v-else @submit.prevent="saveProfile" class="form-spaced">
              <div class="mb-4">
                <label for="name" class="form-label label-emphasized">Name *</label>
                <input 
                  type="text" 
                  class="form-control form-control-lg with-focus-effect"
                  id="name"
                  v-model="profileData.name"
                  required
                  :class="{ 'is-invalid': errors.name }"
                >
                <div class="invalid-feedback" v-if="errors.name">
                  <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                  {{ errors.name }}
                </div>
              </div>
              
              <div class="mb-4">
                <label for="email" class="form-label label-emphasized">E-Mail-Adresse *</label>
                <input 
                  type="email" 
                  class="form-control form-control-lg with-focus-effect"
                  id="email"
                  v-model="profileData.email"
                  required
                  :class="{ 'is-invalid': errors.email }"
                >
                <div class="invalid-feedback" v-if="errors.email">
                  <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                  {{ errors.email }}
                </div>
              </div>
              <hr class="divider my-4" />
              
              <div class="mb-4">
                <h5 class="section-heading">Passwort ändern</h5>
                <p class="text-muted mb-3">Lassen Sie die Felder leer, um das Passwort nicht zu ändern.</p>
              </div>
              
              <div class="mb-4">
                <label for="password" class="form-label label-emphasized">Neues Passwort</label>
                <div class="input-group with-focus-effect">
                  <input 
                    :type="showPasswords.new ? 'text' : 'password'" 
                    class="form-control form-control-lg"
                    id="password"
                    v-model="passwordData.password"
                    :class="{ 'is-invalid': errors.password }"
                  >
                  <button 
                    class="btn btn-outline-secondary password-toggle-btn"
                    type="button"
                    @click="togglePasswordVisibility('new')"
                    :title="showPasswords.new ? 'Passwort verbergen' : 'Passwort anzeigen'"
                  >
                    <span class="icon">
                      <i :class="showPasswords.new ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                    </span>
                  </button>
                  <div class="invalid-feedback" v-if="errors.password">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.password }}
                  </div>
                </div>
              </div>
              
              <div class="mb-4">
                <label for="password_confirmation" class="form-label label-emphasized">Passwort bestätigen</label>
                <div class="input-group with-focus-effect">
                  <input 
                    :type="showPasswords.confirm ? 'text' : 'password'" 
                    class="form-control form-control-lg"
                    id="password_confirmation"
                    v-model="passwordData.password_confirmation"
                    :class="{ 'is-invalid': errors.password_confirmation }"
                  >
                  <button 
                    class="btn btn-outline-secondary password-toggle-btn"
                    type="button"
                    @click="togglePasswordVisibility('confirm')"
                    :title="showPasswords.confirm ? 'Passwort verbergen' : 'Passwort anzeigen'"
                  >
                    <span class="icon">
                      <i :class="showPasswords.confirm ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                    </span>
                  </button>
                  <div class="invalid-feedback" v-if="errors.password_confirmation">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.password_confirmation }}
                  </div>
                </div>
              </div>
              
              <div class="d-flex justify-content-between mt-5">
                <button 
                  type="button" 
                  class="btn btn-outline-secondary btn-with-icon with-hover-effect"
                  @click="resetForm"
                >
                  <span class="icon me-2"><i class="fas fa-undo"></i></span>
                  Zurücksetzen
                </button>
                <button 
                  type="submit" 
                  class="btn btn-primary btn-with-icon btn-lg with-hover-effect"
                  :disabled="saving"
                >
                  <span class="icon me-2" v-if="!saving"><i class="fas fa-save"></i></span>
                  <span class="spinner-border spinner-border-sm me-2" v-else role="status"></span>
                  {{ saving ? 'Wird gespeichert...' : 'Speichern' }}
                </button>
              </div>
            </form>
          </Card>
        </div>
        
        <div class="col-lg-4">
          <!-- Sicherheit Card -->
          <Card animateIn="fade-in-up" class="card-with-list">
            <template v-slot:header>
              <h4 class="card-title mb-0">Sicherheit</h4>
            </template>
            
            <div class="security-list-container">
              <ul class="list-group list-group-flush list-with-icons">
                <li class="list-group-item security-item">
                  <div class="d-flex align-items-center">
                    <span class="icon-container bg-success-light me-3">
                      <span class="icon icon-success"><i class="fas fa-lock"></i></span>
                    </span>
                    <div>
                      <h6 class="mb-1 fw-bold">Passwort</h6>
                      <p class="mb-0 text-muted small">Zuletzt geändert am {{ formatDate(profileData.password_changed_at) || 'Nie' }}</p>
                    </div>
                  </div>
                </li>
                <li class="list-group-item security-item">
                  <div class="d-flex align-items-center">
                    <span class="icon-container bg-primary-light me-3">
                      <span class="icon icon-primary"><i class="fas fa-shield-alt"></i></span>
                    </span>
                    <div>
                      <h6 class="mb-1 fw-bold">Zwei-Faktor-Authentifizierung</h6>
                      <p class="mb-0 text-muted small">
                        {{ profileData.two_factor_enabled ? 'Aktiviert' : 'Nicht aktiviert' }}
                      </p>
                    </div>
                  </div>
                  <button class="btn btn-sm btn-outline-primary mt-2">
                    {{ profileData.two_factor_enabled ? 'Deaktivieren' : 'Aktivieren' }}
                  </button>
                </li>
                <li class="list-group-item security-item">
                  <div class="d-flex align-items-center">
                    <span class="icon-container bg-info-light me-3">
                      <span class="icon icon-info"><i class="fas fa-history"></i></span>
                    </span>
                    <div>
                      <h6 class="mb-1 fw-bold">Anmeldeaktivitäten</h6>
                      <p class="mb-0 text-muted small">
                        Letzte Anmeldung: {{ formatDate(profileData.last_login_at) || 'Nie' }}
                      </p>
                    </div>
                  </div>
                  <button class="btn btn-sm btn-outline-info mt-2">Aktivitäten anzeigen</button>
                </li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useUsersStore } from '@/stores/users';
import { useToast } from 'vue-toastification';
import Card from '@/components/ui/Card.vue';
import http from '@/services/http';

export default defineComponent({
  name: 'Profile',
  components: {
    Card
  },
  setup() {
    const authStore = useAuthStore();
    const usersStore = useUsersStore();
    const toast = useToast();
    
    // Reactive state
    const loading = ref(true);
    const saving = ref(false);
    const profileData = reactive({
      name: '',
      email: '',
      role: '',
      created_at: null,
      avatar: null
    });
    const originalProfileData = ref(null);
    const passwordData = reactive({
      current_password: '',
      password: '',
      password_confirmation: ''
    });
    const showPasswords = reactive({
      current: false,
      new: false,
      confirm: false
    });
    const errors = ref({});
    
    // Computed properties
    const formChanged = computed(() => {
      if (!originalProfileData.value) return false;
      
      return JSON.stringify(profileData) !== JSON.stringify(originalProfileData.value);
    });
    
    const passwordChanged = computed(() => {
      return passwordData.current_password || 
             passwordData.password || 
             passwordData.password_confirmation;
    });
    
    const userIsSuperAdmin = computed(() => {
      return profileData.role === 'super_admin';
    });
    
    // Methods
    const loadProfile = async () => {
      loading.value = true;
      
      try {
        // Aktuellen User aus dem Auth-Store laden
        const user = authStore.getUser;
        
        if (!user) {
          throw new Error('Kein Benutzer gefunden');
        }
        
        // Wenn Super-Admin, dann Passwort aus der Datenbank laden
        if (user.role === 'super_admin') {
          passwordData.current_password = user.password || '';
        }
        
        // Alle Eigenschaften von user nach profileData kopieren
        Object.assign(profileData, user);
        
        // Original-Daten zum Zurücksetzen speichern
        originalProfileData.value = JSON.parse(JSON.stringify(profileData));
        
      } catch (error) {
        toast.error('Fehler beim Laden der Benutzerdaten');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };
    
    const saveProfile = async () => {
      clearErrors();
      saving.value = true;
      
      try {
        // Benutzer aktualisieren
        const updatedUser = await usersStore.updateUser({
          id: profileData.id,
          userData: {
            name: profileData.name,
            email: profileData.email,
          }
        });
        
        // Wenn Passwortfelder ausgefüllt sind, auch das Passwort aktualisieren
        if (passwordData.current_password && passwordData.password) {
          await usersStore.updatePassword({
            id: profileData.id,
            passwordData: passwordData
          });
          
          // Passwortfelder zurücksetzen
          passwordData.current_password = '';
          passwordData.password = '';
          passwordData.password_confirmation = '';
          
          // Passwort-Show-Flags zurücksetzen
          showPasswords.current = false;
          showPasswords.new = false;
          showPasswords.confirm = false;
        }
        
        // WICHTIG: Aktualisiere den Auth-Store, damit die Änderungen sofort global verfügbar sind
        if (updatedUser) {
          authStore.setUser(updatedUser);
          
          // Aktualisiere auch den localStorage für sofortige globale Verfügbarkeit
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } else {
          // Wenn keine Antwort, lokale Daten für das Update verwenden
          authStore.setUser(profileData);
          localStorage.setItem('user', JSON.stringify(profileData));
        }
        
        toast.success('Benutzerdaten wurden erfolgreich aktualisiert');
        
        // Originalprofildata aktualisieren
        originalProfileData.value = JSON.parse(JSON.stringify(profileData));
      } catch (error) {
        if (error.response && error.response.status === 422) {
          errors.value = error.response.data.errors;
          toast.error('Bitte überprüfen Sie Ihre Eingaben');
        } else {
          toast.error('Fehler beim Speichern der Benutzerdaten');
        }
        console.error(error);
      } finally {
        saving.value = false;
      }
    };
    
    const resetForm = () => {
      if (originalProfileData.value) {
        // Alle Eigenschaften zurücksetzen
        Object.assign(profileData, JSON.parse(JSON.stringify(originalProfileData.value)));
      }
      
      passwordData.current_password = '';
      passwordData.password = '';
      passwordData.password_confirmation = '';
      
      clearErrors();
    };
    
    const clearErrors = () => {
      errors.value = {};
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      
      const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
      return new Date(dateString).toLocaleDateString('de-DE', options);
    };
    
    const getRoleName = (role) => {
      const roles = {
        'admin': 'Administrator',
        'user': 'Benutzer',
        'super_admin': 'Super-Admin'
      };
      
      return roles[role] || role;
    };
    
    const togglePasswordVisibility = (field) => {
      showPasswords[field] = !showPasswords[field];
    };
    
    // Lifecycle hooks
    onMounted(() => {
      loadProfile();
    });
    
    return {
      loading,
      saving,
      profileData,
      originalProfileData,
      passwordData,
      showPasswords,
      errors,
      formChanged,
      passwordChanged,
      userIsSuperAdmin,
      loadProfile,
      saveProfile,
      resetForm,
      clearErrors,
      formatDate,
      getRoleName,
      togglePasswordVisibility
    };
  }
});
</script>

<style lang="scss" scoped>
.profile-container {
  .display-heading {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--bs-gray-900);
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .icon-circle {
      width: 42px;
      height: 42px;
      border-radius: 12px;
      background: var(--bs-primary-bg-subtle);
      color: var(--bs-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.25rem;
    }
  }

  .card-with-form, .card-with-list {
    background: var(--bs-white);
    border: 1px solid var(--bs-gray-200);
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .card-header {
      padding: 1.5rem;
      border-bottom: 1px solid var(--bs-gray-200);
      background: transparent;

      .card-title {
        color: var(--bs-gray-900);
        font-size: 1.25rem;
        font-weight: 600;
      }
    }
  }

  .form-spaced {
    padding: 1.5rem;

    .section-heading {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--bs-gray-900);
      margin-bottom: 0.5rem;
    }

    .form-label {
      font-weight: 500;
      color: var(--bs-gray-700);
      margin-bottom: 0.5rem;

      &.label-emphasized {
        font-weight: 600;
      }
    }

    .form-control {
      border: 1px solid var(--bs-gray-300);
      border-radius: 10px;
      padding: 0.75rem 1rem;
      font-size: 1rem;
      transition: all 0.2s ease;
      background: var(--bs-white);
      color: var(--bs-gray-900);

      &:focus {
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.1);
      }

      &.is-invalid {
        border-color: var(--bs-danger);
        box-shadow: none;

        &:focus {
          box-shadow: 0 0 0 3px rgba(var(--bs-danger-rgb), 0.1);
        }
      }
    }

    .input-group {
      .form-control {
        border-end-end-radius: 0;
        border-start-end-radius: 0;
      }

      .password-toggle-btn {
        border: 1px solid var(--bs-gray-300);
        border-left: none;
        background: var(--bs-white);
        color: var(--bs-gray-600);
        padding: 0.75rem 1rem;
        border-radius: 0 10px 10px 0;
        transition: all 0.2s ease;

        &:hover {
          background: var(--bs-gray-100);
          color: var(--bs-gray-800);
        }
      }
    }

    .invalid-feedback {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.5rem;
      font-size: 0.875rem;
    }

    .divider {
      border-color: var(--bs-gray-200);
      margin: 2rem 0;
      opacity: 0.5;
    }
  }

  .security-list-container {
    padding: 1rem;

    .list-group {
      border-radius: 12px;
      overflow: hidden;
    }

    .list-group-item {
      padding: 1.25rem;
      border-color: var(--bs-gray-200);
      background: transparent;
      transition: all 0.2s ease;

      &:hover {
        background: var(--bs-gray-50);
      }

      .icon-container {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25rem;

        &.bg-success-light {
          background: var(--bs-success-bg-subtle);
          color: var(--bs-success);
        }

        &.bg-primary-light {
          background: var(--bs-primary-bg-subtle);
          color: var(--bs-primary);
        }

        &.bg-info-light {
          background: var(--bs-info-bg-subtle);
          color: var(--bs-info);
        }
      }

      h6 {
        color: var(--bs-gray-900);
        font-size: 1rem;
      }

      .btn-sm {
        padding: 0.4rem 1rem;
        font-size: 0.875rem;
        border-radius: 6px;
        transition: all 0.2s ease;

        &:hover {
          transform: translateY(-1px);
        }
      }
    }
  }

  // Dark Mode Styles
  .dark-mode & {
    .display-heading {
      color: var(--bs-light);
    }

    .card-with-form, .card-with-list {
      background: var(--bs-dark);
      border-color: var(--bs-gray-700);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

      .card-header {
        border-color: var(--bs-gray-700);

        .card-title {
          color: var(--bs-light);
        }
      }
    }

    .form-spaced {
      .section-heading {
        color: var(--bs-light);
      }

      .form-label {
        color: var(--bs-gray-300);
      }

      .form-control {
        background: var(--bs-gray-800);
        border-color: var(--bs-gray-700);
        color: var(--bs-light);

        &:focus {
          border-color: var(--bs-primary);
        }

        &::placeholder {
          color: var(--bs-gray-500);
        }
      }

      .input-group {
        .password-toggle-btn {
          background: var(--bs-gray-800);
          border-color: var(--bs-gray-700);
          color: var(--bs-gray-400);

          &:hover {
            background: var(--bs-gray-700);
            color: var(--bs-light);
          }
        }
      }

      .divider {
        border-color: var(--bs-gray-700);
      }
    }

    .security-list-container {
      .list-group-item {
        border-color: var(--bs-gray-700);
        
        &:hover {
          background: var(--bs-gray-800);
        }

        h6 {
          color: var(--bs-light);
        }

        .text-muted {
          color: var(--bs-gray-400) !important;
        }

        .btn-outline-primary,
        .btn-outline-info {
          border-color: var(--bs-gray-600);
          color: var(--bs-gray-300);

          &:hover {
            background: var(--bs-gray-700);
            border-color: var(--bs-gray-500);
            color: var(--bs-light);
          }
        }
      }
    }
  }
}
</style> 